import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { k8sResourceService } from '@/services/k8sResourceService'
import { useAuth } from '@/hooks/useAuth'

interface LicenseContextType {
  // expired is true when the platform license is no longer usable (the
  // operator marked every License CRD as Expired/Invalid). The backend is the
  // authoritative gate; this flag only drives UI redirects and banners.
  expired: boolean
  loading: boolean
  refresh: () => Promise<void>
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined)

// License phases that mean the license is still usable (see synstream-operator).
const USABLE_PHASES = ['Valid', 'Trial', 'FlowLimitExceeded']

// Name of the authoritative platform License CRD. The backend gate reads this
// exact CRD (auth LICENSE_CRD_NAME, default "synstream-license", the CRD the
// operator creates and manages), so the UI mirrors it instead of aggregating
// across every License.
const PRIMARY_LICENSE_NAME = 'synstream-license'

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [expired, setExpired] = useState(false)
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setExpired(false)
      return
    }
    setLoading(true)
    try {
      const list = await k8sResourceService.getLicenses()
      const items = Array.isArray(list?.items) ? list.items : []
      // Mirror the backend: judge the authoritative "license" CRD (fall back to
      // the first one if it is absent). Fail-safe: if there is no license or the
      // read fails, do not lock the UI.
      const primary = items.find((lic: any) => lic?.metadata?.name === PRIMARY_LICENSE_NAME) ?? items[0]
      const phase = primary?.status?.phase
      setExpired(!!primary && !USABLE_PHASES.includes(phase))
    } catch {
      setExpired(false)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Poll in both directions, at different rates.
  //
  // Expired -> valid is polled quickly so the UI unlocks shortly after the
  // owner renews (the operator needs a few seconds to reconcile spec ->
  // status), otherwise PipelineLicenseGate keeps redirecting to /plans after a
  // renewal that already worked.
  //
  // Valid -> expired has to be polled too. This used to return early while the
  // license was still valid, so a session opened before the expiry never
  // noticed it: the sidebar kept offering every product and the routes stayed
  // open until someone happened to reload the page. An expiry that only takes
  // effect on the next page load is not a gate. The slower rate is deliberate:
  // nothing is being withheld from the user in this direction, so there is no
  // reason to poll the API every few seconds all day.
  useEffect(() => {
    if (!isAuthenticated) return
    const id = setInterval(() => { refresh() }, expired ? 8000 : 60000)
    return () => clearInterval(id)
  }, [isAuthenticated, expired, refresh])

  return (
    <LicenseContext.Provider value={{ expired, loading, refresh }}>
      {children}
    </LicenseContext.Provider>
  )
}

export function useLicense() {
  const ctx = useContext(LicenseContext)
  if (!ctx) {
    throw new Error('useLicense must be used within LicenseProvider')
  }
  return ctx
}
